import { useState, useRef } from 'react';
import {
  Stack, TextField, Button, Paper, Popper, ClickAwayListener,
  MenuItem, Checkbox, ListItemText
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { STATUSES } from '../utils/constants';

const PoFilters = ({
  dateRangeStart, setDateRangeStart,
  dateRangeEnd, setDateRangeEnd,
  statusFilter, setStatusFilter,
  assignedToFilter, setAssignedToFilter,
  typeFilter, setTypeFilter,
  daysMoreThan, setDaysMoreThan,
  daysLessThan, setDaysLessThan,
  flaggedOnly, setFlaggedOnly,
  showArchived, setShowArchived,
  poRows
}) => {
  const clearAllFilters = () => {
    setDateRangeStart(null);
    setDateRangeEnd(null);
    setStatusFilter([]);
    setAssignedToFilter([]);
    setTypeFilter([]);
    setDaysMoreThan('');
    setDaysLessThan('');
    setFlaggedOnly(false);
    setShowArchived(false);
  };

  const anyActiveFilter =
    dateRangeStart ||
    dateRangeEnd ||
    statusFilter.length > 0 ||
    assignedToFilter.length > 0 ||
    typeFilter.length > 0 ||
    daysMoreThan ||
    daysLessThan ||
    flaggedOnly ||
    showArchived;

  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const openDateMenu = Boolean(dateAnchorEl);
  const hasDateFilter = dateRangeStart || dateRangeEnd;

  const handleOpenDateMenu = (event) => setDateAnchorEl(event.currentTarget);
  const handleCloseDateMenu = () => setDateAnchorEl(null);
  const clearDateRange = () => {
    setDateRangeStart(null);
    setDateRangeEnd(null);
    handleCloseDateMenu();
  };

  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusSearch, setStatusSearch] = useState('');
  const openStatusMenu = Boolean(statusAnchorEl);
  const statusInputRef = useRef(null);
  const hasStatusFilter = statusFilter.length > 0;
  const toggleStatus = (val) => {
    setStatusFilter((prev) =>
      prev.includes(val) ? prev.filter(s => s !== val) : [...prev, val]
    );
  };
  const clearStatusFilter = () => {
    setStatusFilter([]);
    handleCloseStatusMenu();
  };
  const handleOpenStatusMenu = (e) => {
    setStatusAnchorEl(e.currentTarget);
    setTimeout(() => statusInputRef.current?.focus(), 0);
  };
  const handleCloseStatusMenu = () => {
    setStatusAnchorEl(null);
    setStatusSearch('');
  };

  const [assignedAnchorEl, setAssignedAnchorEl] = useState(null);
  const [assignedSearch, setAssignedSearch] = useState('');
  const openAssignedMenu = Boolean(assignedAnchorEl);
  const assignedInputRef = useRef(null);
  const hasAssignedFilter = assignedToFilter.length > 0;
  const toggleAssigned = (val) => {
    setAssignedToFilter((prev) =>
      prev.includes(val) ? prev.filter(a => a !== val) : [...prev, val]
    );
  };
  const clearAssignedFilter = () => {
    setAssignedToFilter([]);
    handleCloseAssignedMenu();
  };
  const handleOpenAssignedMenu = (e) => {
    setAssignedAnchorEl(e.currentTarget);
    setTimeout(() => assignedInputRef.current?.focus(), 0);
  };
  const handleCloseAssignedMenu = () => {
    setAssignedAnchorEl(null);
    setAssignedSearch('');
  };

  const [typeAnchorEl, setTypeAnchorEl] = useState(null);
  const [typeSearch, setTypeSearch] = useState('');
  const openTypeMenu = Boolean(typeAnchorEl);
  const typeInputRef = useRef(null);
  const hasTypeFilter = typeFilter.length > 0;
  const toggleType = (val) => {
    setTypeFilter((prev) =>
      prev.includes(val) ? prev.filter(a => a !== val) : [...prev, val]
    );
  };
  const clearTypeFilter = () => {
    setTypeFilter([]);
    handleCloseTypeMenu();
  };
  const handleOpenTypeMenu = (e) => {
    setTypeAnchorEl(e.currentTarget);
    setTimeout(() => typeInputRef.current?.focus(), 0);
  };
  const handleCloseTypeMenu = () => {
    setTypeAnchorEl(null);
    setTypeSearch('');
  };

  const [daysAnchorEl, setDaysAnchorEl] = useState(null);
  const openDaysMenu = Boolean(daysAnchorEl);
  const hasDaysFilter = daysMoreThan !== '' || daysLessThan !== '';
  const handleOpenDaysMenu = (e) => setDaysAnchorEl(e.currentTarget);
  const handleCloseDaysMenu = () => setDaysAnchorEl(null);
  const clearDaysFilter = () => {
    setDaysMoreThan('');
    setDaysLessThan('');
    handleCloseDaysMenu();
  };

  const rowStatuses = new Set(poRows.map(r => r.status));
  const allStatuses = Object.values(STATUSES).filter(s => rowStatuses.has(s));
  const filteredStatuses = allStatuses.filter(s =>
    s.toLowerCase().includes(statusSearch.toLowerCase())
  );

  const allAssigned = Array.from(new Set(poRows.map(r => r.assigned_to.role !== 'Completed' ? r.assigned_to.role : null).filter(Boolean)));
  const filteredAssigned = allAssigned.filter(name =>
    name.toLowerCase().includes(assignedSearch.toLowerCase())
  );

  const allTypes = Array.from(new Set(poRows.map(r => r.type)));
  const filteredTypes = allTypes.filter(type =>
    type.toLowerCase().includes(typeSearch.toLowerCase())
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" spacing={2} sx={{ my: 1, flexWrap: 'wrap' }}>
        {/* Type Filter */}
        <div>
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpenTypeMenu}
            sx={{ ...(hasTypeFilter && { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }) }}
          >
            Type
          </Button>
          <Popper open={openTypeMenu} anchorEl={typeAnchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={handleCloseTypeMenu}>
              <Paper sx={{ p: 2, width: 'fit-content', minWidth: 240 }}>
                <TextField
                  inputRef={typeInputRef}
                  variant="outlined"
                  size="small"
                  value={typeSearch}
                  onChange={(e) => setTypeSearch(e.target.value)}
                  placeholder="Search..."
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <Stack spacing={0} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {filteredTypes.map((type) => (
                    <MenuItem key={type} onClick={() => toggleType(type)}>
                      <Checkbox checked={typeFilter.includes(type)} size="small" />
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="center" spacing={2} pt={1}>
                  <Button size="small" variant="outlined" onClick={clearTypeFilter} sx={{ bgcolor: 'gray', color: 'white'}}>Clear</Button>
                  <Button size="small" variant="contained" onClick={handleCloseTypeMenu}>Close</Button>
                </Stack>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </div>

        {/* Date Filter */}
        <div>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CalendarTodayIcon />}
            onClick={handleOpenDateMenu}
            sx={{ ...(hasDateFilter && { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }) }}
          >
            Created On
          </Button>
          <Popper open={openDateMenu} anchorEl={dateAnchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={handleCloseDateMenu}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, width: 250 }}>
                <DatePicker
                  label="Start Date"
                  value={dateRangeStart}
                  onChange={() => {}}
                  onAccept={(newValue) => setDateRangeStart(newValue)}
                  views={['year', 'month', 'day']}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                  label="End Date"
                  value={dateRangeEnd}
                  onChange={() => {}}
                  onAccept={(newValue) => setDateRangeEnd(newValue)}
                  views={['year', 'month', 'day']}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <Stack direction="row" justifyContent="center" spacing={2} pt={1}>
                  <Button size="small" variant="outlined" onClick={clearDateRange} sx={{ bgcolor: 'gray', color: 'white'}}>Clear</Button>
                  <Button size="small" variant="contained" onClick={handleCloseDateMenu}>Close</Button>
                </Stack>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </div>

        {/* Status Filter */}
        <div>
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpenStatusMenu}
            sx={{ ...(hasStatusFilter && { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }) }}
          >
            Status
          </Button>
          <Popper open={openStatusMenu} anchorEl={statusAnchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={handleCloseStatusMenu}>
              <Paper sx={{ p: 2, width: 'fit-content', minWidth: 240 }}>
                <TextField
                  inputRef={statusInputRef}
                  variant="outlined"
                  size="small"
                  value={statusSearch}
                  onChange={(e) => setStatusSearch(e.target.value)}
                  placeholder="Search..."
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <Stack spacing={0} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {filteredStatuses.map((status) => (
                    <MenuItem key={status} onClick={() => toggleStatus(status)}>
                      <Checkbox checked={statusFilter.includes(status)} size="small" />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="center" spacing={2} pt={1}>
                  <Button size="small" variant="outlined" onClick={clearStatusFilter} sx={{ bgcolor: 'gray', color: 'white'}}>Clear</Button>
                  <Button size="small" variant="contained" onClick={handleCloseStatusMenu}>Close</Button>
                </Stack>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </div>

        {/* Assigned To Filter */}
        <div>
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpenAssignedMenu}
            sx={{ ...(hasAssignedFilter && { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }) }}
          >
            Assigned To
          </Button>
          <Popper open={openAssignedMenu} anchorEl={assignedAnchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={handleCloseAssignedMenu}>
              <Paper sx={{ p: 2, width: 'fit-content', minWidth: 240 }}>
                <TextField
                  inputRef={assignedInputRef}
                  variant="outlined"
                  size="small"
                  value={assignedSearch}
                  onChange={(e) => setAssignedSearch(e.target.value)}
                  placeholder="Search..."
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <Stack spacing={0} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {filteredAssigned.map((name) => (
                    <MenuItem key={name} onClick={() => toggleAssigned(name)}>
                      <Checkbox checked={assignedToFilter.includes(name)} size="small" />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="center" spacing={2} pt={1}>
                  <Button size="small" variant="outlined" onClick={clearAssignedFilter} sx={{ bgcolor: 'gray', color: 'white'}}>Clear</Button>
                  <Button size="small" variant="contained" onClick={handleCloseAssignedMenu}>Close</Button>
                </Stack>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </div>

        {/* Days Assigned Filter */}
        <div>
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpenDaysMenu}
            sx={{ ...(hasDaysFilter && { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }) }}
          >
            Days Assigned
          </Button>
          <Popper open={openDaysMenu} anchorEl={daysAnchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={handleCloseDaysMenu}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, width: 220 }}>
                <TextField
                  size="small"
                  label="More than"
                  type="number"
                  value={daysMoreThan}
                  onChange={(e) => setDaysMoreThan(e.target.value)}
                />
                <TextField
                  size="small"
                  label="Less than"
                  type="number"
                  value={daysLessThan}
                  onChange={(e) => setDaysLessThan(e.target.value)}
                />
                <Stack direction="row" justifyContent="center" spacing={2} pt={1}>
                  <Button size="small" variant="outlined" onClick={clearDaysFilter} sx={{ bgcolor: 'gray', color: 'white'}}>Clear</Button>
                  <Button size="small" variant="contained" onClick={handleCloseDaysMenu}>Close</Button>
                </Stack>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </div>

        {/* Flagged Filter */}
        <div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setFlaggedOnly(prev => !prev)}
            sx={{
              ...(flaggedOnly && {
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              })
            }}
          >
            {`Flagged: ${flaggedOnly ? 'Only' : 'All'}`}
          </Button>
        </div>

        {/* Archived Filter */}
        <div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowArchived(prev => !prev)}
            sx={{
              ...(showArchived && {
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              })
            }}
          >
            {`Archived: ${showArchived ? 'Show' : 'Hide'}`}
          </Button>
        </div>

        {anyActiveFilter && (
          <Button
            size="small"
            variant="contained"
            sx={{ bgcolor: 'gray', color: 'white', ml: 'auto' }}
            onClick={clearAllFilters}
          >
            Clear Filters
          </Button>
        )}
      </Stack>
    </LocalizationProvider>
  );
};

export default PoFilters;